import { Component, Inject, Input, ViewChild, ViewContainerRef, } from '@angular/core';
import { Observable } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { PLACEHOLDER_MISSING_COMPONENT_COMPONENT } from '../services/placeholder.token';
import { RawComponent } from './raw.component';
import { isRawRendering } from './rendering';
import * as i0 from "@angular/core";
import * as i1 from "../services/jss-component-factory.service";
/**
 * Renders a single JSS component given a rendering definition.
 * Useful inside templated placeholders.
 */
export class RenderComponentComponent {
    differs;
    componentFactory;
    missingComponentComponent;
    rendering;
    outputs;
    view;
    _inputs;
    _differ;
    destroyed = false;
    constructor(differs, componentFactory, missingComponentComponent) {
        this.differs = differs;
        this.componentFactory = componentFactory;
        this.missingComponentComponent = missingComponentComponent;
    }
    set inputs(value) {
        this._inputs = value;
        if (!this._differ && value) {
            this._differ = this.differs.find(value).create();
        }
    }
    ngOnChanges(changes) {
        if (changes.rendering) {
            this._render();
        }
    }
    _setComponentInputs(componentInstance, inputs) {
        Object.entries(inputs).forEach(([input, inputValue]) => (componentInstance[input] = inputValue));
    }
    _subscribeComponentOutputs(componentInstance, outputs) {
        Object.keys(outputs)
            .filter((output) => componentInstance[output] && componentInstance[output] instanceof Observable)
            .forEach((output) => componentInstance[output]
            .pipe(takeWhile(() => !this.destroyed))
            .subscribe(outputs[output]));
    }
    _render() {
        this.view.clear();
        if (!this.rendering) {
            return;
        }
        const resolveComponent = isRawRendering(this.rendering)
            ? Promise.resolve({
                componentImplementation: RawComponent,
                componentDefinition: this.rendering,
            })
            : this.componentFactory.getComponent(this.rendering);
        resolveComponent.then((rendering) => {
            if (!rendering.componentImplementation) {
                const componentName = rendering.componentDefinition.componentName;
                console.error(`Attempted to render unknown component ${componentName}.`, `Ensure component is mapped, like:
          JssModule.withComponents([
            { name: '${componentName}', type: ${componentName}Component }
          ])`);
                rendering.componentImplementation = this.missingComponentComponent;
            }
            const componentInstance = this.view.createComponent(rendering.componentImplementation)
                .instance;
            componentInstance.rendering = rendering.componentDefinition;
            if (this._inputs) {
                this._setComponentInputs(componentInstance, this._inputs);
            }
            if (this.outputs) {
                this._subscribeComponentOutputs(componentInstance, this.outputs);
            }
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: RenderComponentComponent, deps: [{ token: i0.KeyValueDiffers }, { token: i1.JssComponentFactoryService }, { token: PLACEHOLDER_MISSING_COMPONENT_COMPONENT }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.12", type: RenderComponentComponent, selector: "sc-render-component", inputs: { rendering: "rendering", outputs: "outputs", inputs: "inputs" }, viewQueries: [{ propertyName: "view", first: true, predicate: ["view"], descendants: true, read: ViewContainerRef, static: true }], usesOnChanges: true, ngImport: i0, template: `
    <ng-template #view></ng-template>
  `, isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: RenderComponentComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sc-render-component',
                    template: `
    <ng-template #view></ng-template>
  `,
                }]
        }], ctorParameters: () => [{ type: i0.KeyValueDiffers }, { type: i1.JssComponentFactoryService }, { type: i0.Type, decorators: [{
                    type: Inject,
                    args: [PLACEHOLDER_MISSING_COMPONENT_COMPONENT]
                }] }], propDecorators: { rendering: [{
                type: Input
            }], outputs: [{
                type: Input
            }], view: [{
                type: ViewChild,
                args: ['view', { read: ViewContainerRef, static: true }]
            }], inputs: [{
                type: Input
            }] } });
